import React from "react";
import { createBrowserRouter, redirect } from "react-router";
import { AuthContextProvider } from "./context/auth-context.jsx";
import ErrorBoundary from "./pages/ErrorBoundary.jsx";
import HomePage from "./pages/HomePage.jsx";
import Hotels from "./pages/Hotels.jsx";
import RootLayout from "./pages/Root.jsx";
import WithNavigationLayout from "./pages/WithNavigationLayout.jsx";
import { loader as homePageLoader } from "./pages/loaders/HomePageLoader.js";
import { loader as hotelsPageLoader } from "./pages/loaders/HotelsPageLoader.js";
import { loader as layoutLoader } from "./pages/loaders/NavigationLayoutLoader.js";
import { loader as rootLoader } from "./pages/loaders/RootLoader.js";
import { auth } from "./services/firebase.js";

const withAuthLoader = (loader) => {
  return async (args) => {
    const route = new URL(args.request.url);
    const path = route.pathname;
    const user = await new Promise((resolve, reject) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });
    });
    if (!user) return redirect("/?login=true&from=" + path);

    if (args) args.user = user;
    return loader(args);
  };
};

const router = createBrowserRouter(
  [
    {
      element: <AuthContextProvider />,
      children: [
        {
          path: "/",
          element: <RootLayout />,
          errorElement: <ErrorBoundary />,
          loader: rootLoader,
          shouldRevalidate: ({ currentUrl, nextUrl }) =>
            currentUrl.searchParams.get("lang") !== nextUrl.searchParams.get("lang"),
          children: [
            {
              element: <WithNavigationLayout />,
              loader: layoutLoader,
              children: [
                { index: true, element: <HomePage />, loader: homePageLoader },
                {
                  path: "cheap-flights",
                  shouldRevalidate: ({ nextUrl }) => nextUrl.pathname === "/cheap-flights",
                  lazy: () => import("./pages/CheapFlightsLayout.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/CheapFlightsLoader.js");
                    return loader({ request, params });
                  },
                  children: [
                    {
                      path: ":type/:origin",
                      id: "cheapFlights",
                      shouldRevalidate: ({ nextUrl, currentUrl }) =>
                        nextUrl.toString() !== currentUrl.toString(),
                      lazy: () => import("./pages/CheapFlights.jsx"),
                      async loader({ request, params }) {
                        let { loader } = await import(
                          "./pages/loaders/CheapFlightDealsLoader.js"
                        );
                        return loader({ request, params });
                      },
                      children: [
                        {
                          path: "*",
                          lazy: () => import("./pages/CheapFlights.jsx"),
                        },
                      ],
                    },
                  ],
                },
                { path: "hotels", element: <Hotels />, loader: hotelsPageLoader },
                {
                  path: "charter-flights",
                  lazy: () => import("./pages/CharterFlights.jsx"),
                },
                {
                  path: "favorites",
                  lazy: () => import("./pages/Favorites.jsx"),
                  loader: withAuthLoader(async () => null),
                },
                {
                  path: "bus-tours",
                  lazy: () => import("./pages/ToursCatalog.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/ToursCatalogLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "bus-tours/:code",
                  lazy: () => import("./pages/TourDetails.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/TourDetailsLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "bus-tours/:code/reservation",
                  lazy: () => import("./pages/TourReservation.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/TourReservationLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "bus-tours/:code/checkout",
                  lazy: () => import("./pages/TourCheckout.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/TourCheckoutLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "search/hotels",
                  lazy: () => import("./pages/HotelResults.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/HotelResultsLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "search/hotels/preview/:id",
                  lazy: () => import("./pages/HotelPreview.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/HotelPreviewLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "search/hotels/flights/:id",
                  lazy: () => import("./pages/HotelFlights.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/HotelFlightsLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "business",
                  lazy: () => import("./pages/BusinessBooking.jsx"),
                  children: [
                    {
                      path: "sent",
                      lazy: () => import("./pages/GroupBookingRequestSent.jsx"),
                    },
                  ],
                },
                {
                  path: "groups",
                  lazy: () => import("./pages/GroupBooking.jsx"),
                  children: [
                    {
                      path: "sent",
                      lazy: () => import("./pages/GroupBookingRequestSent.jsx"),
                    },
                  ],
                },
                {
                  path: "search/results",
                  lazy: () => import("./pages/FlightResults.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/FlightResultsLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "dashboard",
                  lazy: () => import("./pages/DashboardLayout.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/DashboardLoader.js");
                    return withAuthLoader(loader)({ request, params });
                  },
                  children: [
                    {
                      path: "trips",
                      lazy: () => import("./pages/dashboard/UserTrips.jsx"),
                      async loader({ request, params }) {
                        let { loader } = await import("./pages/loaders/UserTripsLoader.js");
                        return withAuthLoader(loader)({ request, params });
                      },
                    },
                    {
                      path: "trips/:orderId",
                      lazy: () => import("./pages/dashboard/UserTripDetails.jsx"),
                      async loader({ request, params }) {
                        let { loader } = await import(
                          "./pages/loaders/UserTripDetailsLoader.js"
                        );
                        return withAuthLoader(loader)({ request, params });
                      },
                    },
                    { path: "passengers", lazy: () => import("./pages/TempUnavailable.jsx") },
                    {
                      path: "invoices",
                      lazy: () => import("./pages/dashboard/UserInvoices.jsx"),
                      async loader({ request, params }) {
                        let { loader } = await import("./pages/loaders/UserInvoicesLoader.js");
                        return withAuthLoader(loader)({ request, params });
                      },
                    },
                    { path: "alerts", lazy: () => import("./pages/TempUnavailable.jsx") },
                    { path: "referral", lazy: () => import("./pages/TempUnavailable.jsx") },
                    { path: "preferences", lazy: () => import("./pages/TempUnavailable.jsx") },
                    { path: "support", lazy: () => import("./pages/TempUnavailable.jsx") },
                  ],
                },
                {
                  path: "info/:id",
                  lazy: () => import("./pages/InfoPageLayout.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/InfoPageLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "info/faq",
                  lazy: () => import("./pages/FAQPage.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/FAQPageLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "info/faq-topics",
                  lazy: () => import("./pages/FAQTopicsPage.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/FAQTopicsLoader.js");
                    return loader({ request, params });
                  },
                },
                {
                  path: "about-us",
                  lazy: () => import("./pages/InfoPageLayout.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/InfoPageLoader.js");
                    return loader({ request, params });
                  },
                },
                { path: "*", lazy: () => import("./pages/PageNotFound.jsx") },
              ],
            },
            { path: "booking/cancelled", lazy: () => import("./pages/BookingCancelled.jsx") },
            {
              id: "bookingRouteController",
              path: "booking",
              lazy: () =>
                import("./context/booking-flow-context.jsx").then((m) => ({
                  element: <m.default />,
                })),
              async loader({ request, params }) {
                let { loader } = await import("./pages/loaders/BookingPageLoader.js");
                return loader({ request, params });
              },
              children: [
                {
                  lazy: () => import("./pages/BookingLayoutWrapper.jsx"),
                  children: [
                    {
                      path: ":bookingStep",
                      lazy: () => import("./pages/BookingFlowPage.jsx"),
                    },
                  ],
                },
              ],
            },
            {
              id: "hotelBookingRouteController",
              path: "hotel-booking",
              lazy: () =>
                import("./context/booking-flow-context.jsx").then((m) => ({
                  element: <m.default />,
                })),
              async loader({ request, params }) {
                let { loader } = await import("./pages/loaders/BookingPageLoader.js");
                return loader({ request, params });
              },
              children: [
                {
                  lazy: () => import("./pages/BookingLayoutWrapper.jsx"),
                  children: [
                    {
                      path: ":bookingStep",
                      lazy: () => import("./pages/BookingFlowPage.jsx"),
                    },
                  ],
                },
              ],
            },
            {
              path: "order",
              children: [
                {
                  path: ":orderId/thankyou",
                  lazy: () => import("./pages/OrderComplete.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/OrderCompleteLoader.js");
                    return loader({ request, params });
                  },
                },
                { path: ":orderId/error", lazy: () => import("./pages/OrderError.jsx") },
                {
                  path: ":orderId/confirmation",
                  lazy: () => import("./pages/OfflineOrderConfirmed.jsx"),
                  async loader({ request, params }) {
                    let { loader } = await import("./pages/loaders/OrderReceivedLoader.js");
                    return loader({ request, params });
                  },
                },
                { path: ":orderId/complete", element: <div>this order has been paid</div> },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_startTransition: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

export default router;
